import { useState, useCallback } from 'react';
import { ANALYTICS_EVENTS, trackEvent } from '../analytics';
import { clearPendingIntent, savePendingIntent } from '../utils/pendingIntent';

/**
 * useAuthGate
 * Returns helpers to open/close the AuthModal and a callback wrapper
 * that either runs an action (when authed) or opens the modal (when not).
 *
 * Usage:
 *   const { gateAction, authModalProps } = useAuthGate(user);
 *   <button onClick={gateAction(handleTakeQuiz, AUTH_MESSAGES.QUIZ)}>Take Quiz</button>
 *   <AuthModal {...authModalProps} />
 */
export function useAuthGate(user) {
    const [modalState, setModalState] = useState({ isOpen: false, message: '', intent: null });

    const openModal = useCallback((message, intent = null, { reason } = {}) => {
        if (intent) savePendingIntent(intent);
        setModalState({ isOpen: true, message, intent });
        trackEvent(ANALYTICS_EVENTS.GUEST_AUTH_GATE_SHOWN, { platform: 'web', ...(reason ? { reason } : {}) });
    }, []);

    const closeModal = useCallback(() => {
        clearPendingIntent();
        setModalState((prev) => ({ ...prev, isOpen: false }));
    }, []);

    /**
     * Wraps an action with an auth check.
     * If the user is logged in, the action runs.
     * If not, the AuthModal opens with the provided message.
     * options.reason tags the guest_auth_gate_shown event (e.g. 'rating').
     */
    const gateAction = useCallback((action, message, intent = null, options = {}) => {
        return (...args) => {
            if (user) {
                action(...args);
            } else {
                openModal(message, intent, options);
            }
        };
    }, [user, openModal]);

    return {
        gateAction,
        authModalProps: {
            isOpen: modalState.isOpen,
            message: modalState.message,
            onClose: closeModal,
        },
    };
}
